import { Component, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthenticationService } from 'src/app/services/authentication.service';
import { RestaurantService } from 'src/app/services/restaurant.service';
import { StoreService } from 'src/app/services/store.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  form: FormGroup;
  submitted = false;
  loading = false;
  showPassword = false;
  errorMessage = '';
  private destroy$ = new Subject<void>();
  private loadingSwal = Swal.mixin({
    allowOutsideClick: false,
    allowEscapeKey: false
  });

  constructor(
    private renderer: Renderer2,
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthenticationService,
    private restaurantService: RestaurantService,
    private storeService: StoreService
  ) {
    this.form = this.createLoginForm();
  }

  ngOnInit(): void {
    this.setupPageStyle();
    if (this.authService.isAuthenticated()) {
      this.router.navigate([this.authService.getDefaultDashboard()]);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Create reactive form with validation
   */
  private createLoginForm(): FormGroup {
    return this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.maxLength(60)
        ]
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(30)
        ]
      ]
    });
  }

  /**
   * Configure page styling for login page
   */
  private setupPageStyle(): void {
    this.renderer.removeClass(document.body, 'sidebar-mini');
    this.renderer.removeClass(document.body, 'layout-fixed');
    this.renderer.removeClass(document.body, 'layout-navbar-fixed');
    this.renderer.addClass(document.body, 'login-page');
    this.renderer.removeStyle(document.body, 'height');
  }

  /**
   * Handle login form submission
   */
  loginAction(): void {
    this.submitted = true;
    this.errorMessage = '';

    if (this.form.invalid) {
      this.showValidationError();
      return;
    }

    this.performLogin();
  }

  /**
   * Show validation error message
   */
  private showValidationError(): void {
    const emailControl = this.form.get('email');
    const passwordControl = this.form.get('password');

    if ((emailControl && emailControl.hasError('required')) || (passwordControl && passwordControl.hasError('required'))) {
      this.errorMessage = 'Email and password are required';
    } else if (emailControl && emailControl.hasError('email')) {
      this.errorMessage = 'Please enter a valid email address';
    } else if (passwordControl && passwordControl.hasError('minlength')) {
      this.errorMessage = 'Password must be at least 6 characters';
    } else {
      this.errorMessage = 'Please enter valid data';
    }
  }

  /**
   * Perform login API call and handle response
   */
  private performLogin(): void {
    this.loading = true;
    this.setFormDisabledState(true);
    this.showLoadingDialog();

    const { email, password } = this.form.value;

    this.authService.login(email, password)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        response => this.handleLoginResponse(response),
        error => this.handleLoginError(error)
      );
  }

  /**
   * Handle successful login response
   */
  private handleLoginResponse(response: any): void {
    this.loadingSwal.close();
    this.loading = false;
    this.setFormDisabledState(false);

    if (response.status === 'success') {
      const userData = {
        data: response.data,
        token: response.token,
        is_logged: true
      };
      this.storeService.setLoginData(userData);
      this.navigateByRole(response.data);
    } else {
      this.errorMessage = response.msg || 'Login failed. Please try again.';
      this.loadingSwal.close();
    }
  }

  /**
   * Navigate based on user role
   */
  private navigateByRole(userData: any): void {
    if (userData.user_role === 0) {
      // Admin user
      this.navigateTo('admin/dashboard');
    } else if (userData.user_role === 1) {
      // Restaurant owner - fetch restaurant data
      this.loadRestaurantData(userData);
    } else {
      this.errorMessage = 'Invalid user role';
      this.loadingSwal.close();
    }
  }

  /**
   * Fetch and store restaurant data, then navigate
   */
  private loadRestaurantData(userData: any): void {
    this.restaurantService.getOneRestaurant(userData.restaurant_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        restaurantData => {
          this.storeService.setRestaurant(restaurantData);
          sessionStorage.setItem('restaurant_data', JSON.stringify(restaurantData));
          sessionStorage.setItem('restaurant_id', userData.restaurant_id);
          this.loadingSwal.close();
          this.navigateTo('/restaurant/dashboard');
        },
        error => {
          this.loadingSwal.close();
          this.errorMessage = 'Failed to load restaurant data. Please try again.';
        }
      );
  }

  /**
   * Navigate to route
   */
  private navigateTo(route: string): void {
    this.router.navigate([route]).catch(err => {
      console.error('Navigation error:', err);
      this.errorMessage = 'Navigation failed';
    });
  }

  /**
   * Show loading dialog
   */
  private showLoadingDialog(): void {
    this.loadingSwal.fire({
      customClass: { container: 'progress-swal' },
      title: 'Signing in...',
      onOpen: () => {
        Swal.showLoading();
      }
    });
  }

  /**
   * Handle login errors
   */
  private handleLoginError(error: any): void {
    this.loading = false;
    this.setFormDisabledState(false);
    this.loadingSwal.close();
    this.errorMessage = error.msg || 'Authentication failed. Please try again.';
  }

  private setFormDisabledState(disabled: boolean): void {
    if (disabled) {
      this.form.disable({ emitEvent: false });
      return;
    }
    this.form.enable({ emitEvent: false });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Get form controls for template
   */
  get f() {
    return this.form.controls;
  }

  /**
   * Check if email field has error and was touched or submitted
   */
  hasEmailError(): boolean {
    const emailControl = this.form.get('email');
    return !!(emailControl && emailControl.invalid && (emailControl.dirty || emailControl.touched || this.submitted));
  }

  /**
   * Check if password field has error and was touched or submitted
   */
  hasPasswordError(): boolean {
    const passwordControl = this.form.get('password');
    return !!(passwordControl && passwordControl.invalid && (passwordControl.dirty || passwordControl.touched || this.submitted));
  }
}
